import { useEffect, useRef, useSyncExternalStore } from "react";

import { type ParagraphCommentPairings, pairCommentsWithRedlines } from "../../../utils/api";
import type { CommentPairingAnswer, PendingCommentPairing } from "./paragraph-groups";

const FAILED_PAIRING_RETRY_MS = 60_000;

const answers = new Map<string, CommentPairingAnswer>();
const inFlight = new Set<string>();
const failedAtMs = new Map<string, number>();
const listeners = new Set<() => void>();
let version = 0;

function notify(): void {
  version += 1;
  listeners.forEach((listener) => listener());
}

function answerOf(asked: PendingCommentPairing, pairs: NonNullable<ParagraphCommentPairings["pairs"]>): CommentPairingAnswer | null {
  const redlineByComment = new Map<string, string | null>();
  for (const pair of pairs) {
    const commentId = asked.commentIdByLabel.get(pair.comment_label);
    if (commentId === undefined) return null;
    const redlineLabel = pair.redline_label ?? null;
    if (redlineLabel === null) {
      redlineByComment.set(commentId, null);
      continue;
    }
    const redlineId = asked.redlineIdByLabel.get(redlineLabel);
    if (redlineId === undefined) return null;
    redlineByComment.set(commentId, redlineId);
  }
  return { redlineByComment };
}

export const commentPairings = {
  answerFor(key: string): CommentPairingAnswer | undefined {
    return answers.get(key);
  },
  version(): number {
    return version;
  },
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  async request(authHeader: string, pending: PendingCommentPairing[]): Promise<void> {
    const now = Date.now();
    const toAsk = pending.filter((entry) => {
      const key = entry.request.paragraph_label;
      return !answers.has(key) && !inFlight.has(key) && (failedAtMs.get(key) ?? Number.NEGATIVE_INFINITY) + FAILED_PAIRING_RETRY_MS < now;
    });
    if (toAsk.length === 0) return;
    toAsk.forEach((entry) => inFlight.add(entry.request.paragraph_label));
    try {
      const paragraphs = await pairCommentsWithRedlines(authHeader, toAsk.map((entry) => entry.request));
      for (const paragraph of paragraphs) {
        const asked = toAsk.find((entry) => entry.request.paragraph_label === paragraph.paragraph_label);
        if (asked === undefined) continue;
        const answer = paragraph.pairs === null ? null : answerOf(asked, paragraph.pairs);
        if (answer === null) {
          failedAtMs.set(paragraph.paragraph_label, Date.now());
          continue;
        }
        answers.set(paragraph.paragraph_label, answer);
      }
    } catch {
      toAsk.forEach((entry) => failedAtMs.set(entry.request.paragraph_label, Date.now()));
    } finally {
      toAsk.forEach((entry) => inFlight.delete(entry.request.paragraph_label));
      notify();
    }
  },
  reset(): void {
    answers.clear();
    inFlight.clear();
    failedAtMs.clear();
    notify();
  },
};

export interface CommentPairings {
  version: number;
  answerFor(key: string): CommentPairingAnswer | undefined;
}

export function useCommentPairings(authHeader: string | null, pending: PendingCommentPairing[]): CommentPairings {
  const currentVersion = useSyncExternalStore(commentPairings.subscribe, commentPairings.version, commentPairings.version);
  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  const pendingKey = pending.map((entry) => entry.request.paragraph_label).join("\u0000");
  useEffect(() => {
    if (authHeader === null || pendingRef.current.length === 0) return;
    void commentPairings.request(authHeader, pendingRef.current);
  }, [authHeader, pendingKey, currentVersion]);
  return { version: currentVersion, answerFor: commentPairings.answerFor };
}
